import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";

import { toast } from "@/components/ui/use-toast";
import { useAuth, useConfirmDialog } from "@/context";
import { useI18n } from "@/context/i18n";
import { queryClient } from "@/lib/api";

import * as campaignsApi from "./campaigns.api";
import { IUseCampaigns } from "./campaigns.hooks.interface";
import { campaignsFormSchema, TCampaignsFormData } from "./campaigns.schema";
import {
  mapCampaignFormToCreatePayload,
  mapCampaignFormToUpdatePayload,
} from "./campaigns.utils";

/** organizationIdFromRoute: clientId da rota /clients/:clientId/campaigns (parceiro em contexto) */
export const useCampaigns = (
  organizationIdFromRoute?: string
): IUseCampaigns => {
  const { t } = useI18n();
  const { auth } = useAuth();
  const { openDialog } = useConfirmDialog();

  const organizationId =
    organizationIdFromRoute || auth.organization_root_id || undefined;

  const [isCampaignFormOpen, setIsCampaignFormOpen] =
    React.useState<boolean>(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);

  const campaignsForm = useForm<TCampaignsFormData>({
    resolver: zodResolver(campaignsFormSchema),
    defaultValues: {
      name: "",
      project_id: "",
      plan_ids: [],
      channel: "whatsapp",
      status: "draft",
      organization_id: organizationId,
    },
  });

  const selectedProjectId = campaignsForm.watch("project_id");

  const openCampaignForm = React.useCallback(() => {
    setIsCampaignFormOpen(true);
  }, []);

  const closeCampaignForm = React.useCallback(() => {
    setIsCampaignFormOpen(false);
    setEditingId(null);
    campaignsForm.reset({
      name: "",
      project_id: "",
      plan_ids: [],
      channel: "whatsapp",
      status: "draft",
      organization_id: organizationId,
    });
  }, [organizationId, campaignsForm]);

  const listParams = React.useMemo(
    () => ({
      ...(organizationId && { organization_id: organizationId }),
    }),
    [organizationId]
  );

  const {
    data: campaigns = [],
    isLoading: isCampaignsLoading,
    error: campaignsError,
  } = useQuery({
    queryKey: ["campaigns", listParams],
    queryFn: () => campaignsApi.listCampaigns(listParams),
    enabled: Boolean(organizationId),
  });

  const { data: campaignById } = useQuery({
    queryKey: ["campaigns", editingId],
    queryFn: () => campaignsApi.getCampaignById(editingId || ""),
    enabled: Boolean(editingId),
  });

  const {
    data: projects = [],
    isLoading: isProjectsLoading,
    error: projectsError,
  } = useQuery({
    queryKey: ["campaigns-projects", organizationId],
    queryFn: () => campaignsApi.getProjectsByOrg(organizationId || ""),
    enabled: Boolean(organizationId),
  });

  const {
    data: plans = [],
    isLoading: isPlansLoading,
    error: plansError,
  } = useQuery({
    queryKey: ["campaigns-plans", selectedProjectId],
    queryFn: () => campaignsApi.getPlansByProject(selectedProjectId),
    enabled: Boolean(selectedProjectId),
  });

  const createCampaignMutation = useMutation({
    mutationFn: campaignsApi.createCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast({
        title: t("generic.success"),
        description: t("campaigns.success.created"),
      });
      closeCampaignForm();
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateCampaignMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: ReturnType<typeof mapCampaignFormToUpdatePayload>;
    }) => campaignsApi.updateCampaign(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      if (editingId) {
        queryClient.invalidateQueries({
          queryKey: ["campaigns", editingId],
        });
      }
      toast({
        title: t("generic.success"),
        description: t("campaigns.success.updated"),
      });
      closeCampaignForm();
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteCampaignMutation = useMutation({
    mutationFn: campaignsApi.deleteCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast({
        title: t("generic.success"),
        description: t("campaigns.success.deleted"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleCampaignFormSubmit = React.useCallback(
    (values: TCampaignsFormData) => {
      if (editingId) {
        updateCampaignMutation.mutate({
          id: editingId,
          data: mapCampaignFormToUpdatePayload(values, organizationId),
        });
        return;
      }

      createCampaignMutation.mutate(
        mapCampaignFormToCreatePayload(values, organizationId)
      );
    },
    [
      organizationId,
      createCampaignMutation,
      editingId,
      updateCampaignMutation,
    ]
  );

  const deleteCampaignById = React.useCallback(
    (id: string) => {
      const selectedCampaign = campaigns.find(
        (campaign) => campaign.id === id
      );
      openDialog(
        t("generic.confirmDelete"),
        selectedCampaign?.name || "",
        () => deleteCampaignMutation.mutate(id)
      );
    },
    [campaigns, deleteCampaignMutation, openDialog, t]
  );

  const editCampaign = React.useCallback((id: string) => {
    setEditingId(id);
    setIsCampaignFormOpen(true);
  }, []);

  React.useEffect(() => {
    if (!campaignsError) return;
    toast({
      title: t("generic.error"),
      description: campaignsError.message,
      variant: "destructive",
    });
  }, [campaignsError, t]);

  React.useEffect(() => {
    if (!projectsError) return;
    toast({
      title: t("generic.error"),
      description: t("campaigns.errors.loadingProjects"),
      variant: "destructive",
    });
  }, [projectsError, t]);

  React.useEffect(() => {
    if (!plansError) return;
    toast({
      title: t("generic.error"),
      description: t("campaigns.errors.loadingPlans"),
      variant: "destructive",
    });
  }, [plansError, t]);

  React.useEffect(() => {
    if (!campaignById || !editingId) return;
    campaignsForm.reset({
      name: campaignById.name,
      project_id: campaignById.project_id,
      plan_ids: campaignById.plan_ids || [],
      channel: "whatsapp",
      status: campaignById.status,
      organization_id:
        campaignById.organization_id || organizationId || undefined,
    });
  }, [campaignById, campaignsForm, editingId, organizationId]);

  return {
    campaigns,
    projects,
    plans,
    isCampaignFormOpen,
    isCampaignsLoading,
    isProjectsLoading,
    isPlansLoading,
    openCampaignForm,
    closeCampaignForm,
    editCampaign,
    deleteCampaignById,
    handleCampaignFormSubmit,
    campaignsForm,
    editingId,
  };
};
